import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

/** Self-service login removal for anyone who is not the account owner. */
export const DeleteLoginButton = ({ redirectTo = "/login", note }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await api.delete("/account/me");
      await logout();
      navigate(redirectTo);
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid="delete-login-card" className="rounded-xl border border-red-500/30 bg-red-500/5 p-5">
      <p className="font-mono text-[11px] uppercase tracking-widest text-red-300">Delete my account</p>
      {!open ? (
        <>
          <p className="mt-2 text-sm text-slate-400">
            {note || "Removes your login and sign-in details. Your operator keeps the trip and invoice records they are required to keep."}
          </p>
          <Button data-testid="delete-login-open" variant="outline" onClick={() => setOpen(true)}
            className="mt-4 border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200">
            <Trash2 className="mr-2 h-4 w-4" /> Delete my account
          </Button>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-slate-200">
            This cannot be undone. You will be signed out immediately and will need a new invitation to
            come back.
          </p>
          {error && <p data-testid="delete-login-error" className="mt-3 text-sm text-red-400">{error}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="delete-login-confirm" disabled={busy} onClick={remove}
              className="bg-red-500 text-white hover:bg-red-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, delete my account"}
            </Button>
            <Button data-testid="delete-login-cancel" variant="outline" disabled={busy}
              onClick={() => { setOpen(false); setError(""); }}
              className="border-ink-600 bg-transparent text-slate-300">
              Keep my account
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
